import { addressParser, type Address, type Email } from "postal-mime";
import type { Mailbox } from "../src/headers";

export interface DeliveryAddress { address: string; sources: string[] }
export interface RecipientDetails {
  to: Mailbox[];
  cc: Mailbox[];
  bcc: Mailbox[];
  delivery: DeliveryAddress[];
}

// Les groupes et les en-têtes répétés doivent conserver toutes leurs adresses.
// Chaque catégorie garde son sens : une adresse en Cc ne devient jamais un « À ».
function mailboxes(addresses: Address[] = []): Mailbox[] {
  const unique = new Map<string, Mailbox>();
  for (const entry of addresses) {
    const members = entry.group ?? [entry];
    for (const member of members) {
      if (!member.address) continue;
      const address = member.address.trim().toLowerCase();
      const name = member.name || unique.get(address)?.name || null;
      unique.set(address, { name, address });
    }
  }
  return [...unique.values()];
}

const DELIVERY_HEADERS: Record<string, string> = {
  "delivered-to": "Delivered-To",
  "x-original-to": "X-Original-To",
  "envelope-to": "Envelope-To",
  "x-envelope-to": "X-Envelope-To",
  "x-forwarded-to": "X-Forwarded-To",
  "resent-to": "Resent-To",
  "resent-cc": "Resent-Cc",
  "resent-bcc": "Resent-Bcc",
};

export function recipientDetails(email: Pick<Email, "to" | "cc" | "bcc" | "headers">): RecipientDetails {
  const delivery = new Map<string, DeliveryAddress>();
  const add = (address: string, source: string) => {
    address = address.trim().toLowerCase();
    // Une trace de livraison doit contenir une adresse, pas un serveur, une URL ou du texte.
    if (!/^[^\s<>@\x00-\x1f\x7f]+@[^\s<>@\x00-\x1f\x7f]+$/.test(address)) return;
    const entry = delivery.get(address) ?? { address, sources: [] };
    if (!entry.sources.includes(source)) entry.sources.push(source);
    delivery.set(address, entry);
  };
  for (const header of email.headers) {
    const key = header.key.toLowerCase();
    const source = Object.hasOwn(DELIVERY_HEADERS, key) ? DELIVERY_HEADERS[key] : undefined;
    if (source) {
      for (const mailbox of mailboxes(addressParser(header.value))) add(mailbox.address, source);
    } else if (key === "received") {
      // Seule la clause « for » de fin décrit un destinataire. Les noms/IP des relais,
      // identifiants SMTP et adresses présentes ailleurs dans le texte sont ignorés.
      const recipient = header.value.match(/\bfor\s+(?:<([^<>\s]+@[^<>\s]+)>|([^\s<>;]+@[^\s<>;]+))\s*(?:;|$)/i);
      if (recipient) add(recipient[1] ?? recipient[2], "Received (for)");
    }
  }
  return { to: mailboxes(email.to), cc: mailboxes(email.cc), bcc: mailboxes(email.bcc), delivery: [...delivery.values()] };
}
